import { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import {
  collection, doc, addDoc, getDoc, updateDoc, getDocs, deleteDoc,
  query, where, orderBy, onSnapshot, serverTimestamp, limitToLast,
} from "firebase/firestore";
import { IC, Avatar, useToast } from "../shared";

// ── Relative time helper ─────────────────────────────────────────────────────
const timeAgo = (date) => {
  if (!date) return "";
  const now = new Date();
  const diff = (now - date) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return "Yesterday";
  if (diff < 604800) return `${Math.floor(diff / 86400)} days`;
  return date.toLocaleDateString("en", { month: "short", day: "numeric" });
};

const TWELVE_HOURS = 12 * 60 * 60 * 1000;

const DesktopChat = ({ currentUser, openRequest, onOpenRequestHandled, onActiveChange, openLostChat, onLostChatHandled }) => {
  const toast = useToast();
  const [passengerChats, setPassengerChats] = useState([]);
  const [driverChats, setDriverChats]       = useState([]);
  const [lostItemChats, setLostItemChats]   = useState([]);
  const [active, setActive]                 = useState(null);
  const [messages, setMessages]             = useState([]);
  const [msg, setMsg]                       = useState("");
  const [unreadByChatId, setUnreadByChatId] = useState({});
  const [lastMessages, setLastMessages]     = useState({});
  const [, tick] = useState(0);
  const endRef = useRef(null);

  useEffect(() => {
    const q1 = query(collection(db, "requests"), where("passengerId", "==", currentUser.id));
    const q2 = query(collection(db, "requests"), where("driverId", "==", currentUser.id));
    const filterActive = (docs) => {
      const now = new Date();
      const active = [];
      const completed = [];
      docs.forEach(d => {
        const r = { id: d.id, ...d.data() };
        if (r.status !== "accepted") return;
        if (new Date(r.tripDatetime) <= now) { completed.push(r); return; }
        active.push(r);
      });
      completed.forEach(r => {
        getDocs(collection(db, "chats", r.id, "messages")).then(snap => {
          snap.docs.forEach(m => deleteDoc(doc(db, "chats", r.id, "messages", m.id)));
        }).catch(() => {});
      });
      return active;
    };
    const unsub1 = onSnapshot(q1, snap => setPassengerChats(filterActive(snap.docs)));
    const unsub2 = onSnapshot(q2, snap => setDriverChats(filterActive(snap.docs)));
    const q3 = query(collection(db, "notifications", currentUser.id, "items"), where("type", "==", "message"));
    const unsub3 = onSnapshot(q3, snap => {
      const byChat = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.chatId && !data.read) {
          if (!byChat[data.chatId]) byChat[data.chatId] = [];
          byChat[data.chatId].push(d.id);
        }
      });
      setUnreadByChatId(byChat);
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [currentUser.id]);

  // Listen to lost-item chats
  useEffect(() => {
    const q1 = query(collection(db, "lostItemChats"), where("passengerId", "==", currentUser.id));
    const q2 = query(collection(db, "lostItemChats"), where("driverId", "==", currentUser.id));
    const merge = (s1, s2) => {
      const now = Date.now();
      const all = [...s1, ...s2];
      const seen = new Set();
      return all.filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); const cr = c.createdAt?.toDate?.()?.getTime() || 0; return c.status === "active" && (now - cr) < TWELVE_HOURS; });
    };
    let d1 = [], d2 = [];
    const u1 = onSnapshot(q1, snap => { d1 = snap.docs.map(d => ({ id: d.id, ...d.data(), _lostItem: true })); setLostItemChats(merge(d1, d2)); });
    const u2 = onSnapshot(q2, snap => { d2 = snap.docs.map(d => ({ id: d.id, ...d.data(), _lostItem: true })); setLostItemChats(merge(d1, d2)); });
    const iv = setInterval(() => tick(x => x + 1), 1000);
    return () => { u1(); u2(); clearInterval(iv); };
  }, [currentUser.id]);

  // Auto-open lost-item chat
  useEffect(() => {
    if (openLostChat?.chatId) {
      const chat = lostItemChats.find(c => c.id === openLostChat.chatId);
      setActive(chat || { id: openLostChat.chatId, _lostItem: true, ...openLostChat });
      onActiveChange?.(true);
      onLostChatHandled?.();
    }
  }, [openLostChat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to the latest message for each chat
  const allChats = [...passengerChats, ...driverChats, ...lostItemChats];
  const chatIdsKey = allChats.map(c => c.id).sort().join(",");
  useEffect(() => {
    if (allChats.length === 0) return;
    const unsubs = allChats.map(chat => {
      const colPath = chat._lostItem ? "lostItemChats" : "chats";
      const q = query(
        collection(db, colPath, chat.id, "messages"),
        orderBy("ts", "desc"),
        limitToLast(1)
      );
      return onSnapshot(q, snap => {
        if (snap.docs.length > 0) {
          const d = snap.docs[0].data();
          setLastMessages(prev => ({ ...prev, [chat.id]: { text: d.text, ts: d.ts, senderId: d.senderId } }));
        }
      });
    });
    return () => unsubs.forEach(u => u());
  }, [chatIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (openRequest) {
      openChat(openRequest);
      onOpenRequestHandled?.();
    }
  }, [openRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  const openChat = async (chat) => {
    if (chat._lostItem) {
      setActive(chat); onActiveChange?.(true);
      const notifIds = unreadByChatId[chat.id] || [];
      notifIds.forEach(nid => updateDoc(doc(db, "notifications", currentUser.id, "items", nid), { read: true }));
      return;
    }
    const reqSnap = await getDoc(doc(db, "requests", chat.id));
    if (!reqSnap.exists() || reqSnap.data().status !== "accepted") {
      toast("This chat is no longer available.", "info");
      return;
    }
    setActive(chat);
    onActiveChange?.(true);
    const notifIds = unreadByChatId[chat.id] || [];
    notifIds.forEach(nid => updateDoc(doc(db, "notifications", currentUser.id, "items", nid), { read: true }));
  };

  useEffect(() => {
    if (!active) { setMessages([]); return; }
    const colPath = active._lostItem ? "lostItemChats" : "chats";
    const q = query(collection(db, colPath, active.id, "messages"), orderBy("ts", "asc"));
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
    return unsub;
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMsg = async () => {
    if (!msg.trim() || !active) return;
    const text = msg.trim(); setMsg("");
    const colPath = active._lostItem ? "lostItemChats" : "chats";
    await addDoc(collection(db, colPath, active.id, "messages"), { senderId: currentUser.id, text, ts: serverTimestamp() });
    const otherId = active.passengerId === currentUser.id ? active.driverId : active.passengerId;
    await addDoc(collection(db, "notifications", otherId, "items"), {
      text: active._lostItem ? `${currentUser.name} sent a message about a lost item` : `New message from ${currentUser.name}`,
      tab: "chat", type: active._lostItem ? "lost_item" : "message", chatId: active.id,
      ts: serverTimestamp(), read: false,
    });
  };

  useEffect(() => {
    if (active && !allChats.find(c => c.id === active.id)) {
      setActive(null);
      onActiveChange?.(false);
    }
  }, [allChats, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const getOther = chat => chat.passengerId === currentUser.id
    ? { id: chat.driverId, name: chat.driverName, avatar: chat.driverAvatar, rating: chat.driverRating || 0 }
    : { id: chat.passengerId, name: chat.passengerName, avatar: chat.passengerAvatar, rating: chat.passengerRating || 0 };

  // Sort chats: unread first, then by latest message time
  const sortedChats = [...allChats].sort((a, b) => {
    const ua = unreadByChatId[a.id]?.length || 0;
    const ub = unreadByChatId[b.id]?.length || 0;
    if (ua && !ub) return -1;
    if (!ua && ub) return 1;
    const ta = lastMessages[a.id]?.ts?.toDate?.()?.getTime() || 0;
    const tb = lastMessages[b.id]?.ts?.toDate?.()?.getTime() || 0;
    return tb - ta;
  });

  return (
    <div className="d-chat-wrapper">
      {/* Sidebar: chat list */}
      <div className="d-chat-sidebar">
        <div style={{ padding: "24px 20px 12px" }}>
          <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)", marginBottom: 4 }}>Messages</h2>
          <p style={{ color: "var(--muted)", fontSize: 13 }}>Available after your seat is confirmed.</p>
        </div>
        {sortedChats.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px 20px", color: "var(--muted)" }}>
            <div style={{ fontSize: 48 }}>💬</div>
            <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, marginTop: 12 }}>No chats yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Accepted requests unlock chat</div>
          </div>
        ) : sortedChats.map(chat => {
          const other = getOther(chat);
          const unreadCount = unreadByChatId[chat.id]?.length || 0;
          const isActive = active?.id === chat.id;
          const last = lastMessages[chat.id];
          const lastText = last?.text || null;
          const lastTime = last?.ts?.toDate ? timeAgo(last.ts.toDate()) : "";
          return (
            <div key={chat.id} onClick={() => openChat(chat)} style={{
              display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 20px",
              borderBottom: "1px solid var(--border)", cursor: "pointer", transition: "background .15s",
              background: isActive ? "rgba(43,91,224,.06)" : "transparent",
            }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "var(--warm)"; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? "rgba(43,91,224,.06)" : "transparent"; }}
            >
              <div style={{ position: "relative", flexShrink: 0, marginTop: 2 }}>
                <Avatar user={other} size={44}/>
                {chat._lostItem ? (
                  <div style={{ position: "absolute", top: -4, right: -4, width: 20, height: 20, borderRadius: 7, background: "#f5a623", border: "2px solid var(--card)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </div>
                ) : (
                  <div style={{ position: "absolute", bottom: 0, right: 0, width: 12, height: 12, borderRadius: "50%", background: "var(--success)", border: "2px solid var(--card)" }}/>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Name + time */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                  <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 14, color: isActive ? "var(--accent)" : "var(--ink)" }}>{other.name}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)", flexShrink: 0, marginLeft: 8 }}>{lastTime}</div>
                </div>
                {/* Route + lost item pill */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>{chat.tripFrom} → {chat.tripTo}</span>
                  {chat._lostItem && <span style={{ fontSize: 9, fontFamily: "Syne", fontWeight: 700, padding: "2px 6px", borderRadius: 5, background: "rgba(245,166,35,.12)", color: "#f5a623", textTransform: "uppercase" }}>Lost Item</span>}
                </div>
                {/* Last message */}
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  {lastText && <span style={{ width: 5, height: 5, borderRadius: "50%", background: unreadCount ? "var(--accent)" : "var(--muted)", flexShrink: 0, opacity: unreadCount ? 1 : 0.5 }}/>}
                  <div style={{
                    fontSize: 12, color: unreadCount ? "var(--ink)" : "var(--muted)",
                    fontWeight: unreadCount ? 600 : 400,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {lastText || "No messages yet"}
                  </div>
                </div>
              </div>
              {unreadCount > 0 && (
                <div style={{ background: "var(--accent)", color: "#fff", borderRadius: 10, fontSize: 11, fontWeight: 700, fontFamily: "Syne", padding: "2px 8px", minWidth: 20, textAlign: "center", flexShrink: 0, marginTop: 2 }}>{unreadCount}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Main: chat messages */}
      {!active ? (
        <div className="d-chat-empty">
          <div style={{ fontSize: 48 }}>💬</div>
          <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16 }}>Select a conversation</div>
          <div style={{ fontSize: 13 }}>Pick a chat from the left to start messaging</div>
        </div>
      ) : (() => {
        const other = getOther(active);
        const isDriverView = active.driverId === currentUser.id;
        const getLostTimer = () => {
          if (!active._lostItem || !active.createdAt) return null;
          const cr = active.createdAt?.toDate?.()?.getTime() || (active.createdAt?.seconds ? active.createdAt.seconds * 1000 : 0);
          if (!cr) return null;
          const rem = TWELVE_HOURS - (Date.now() - cr);
          if (rem <= 0) return "0:00:00";
          const hrs = Math.floor(rem / 3600000);
          const mins = Math.floor((rem % 3600000) / 60000);
          const secs = Math.floor((rem % 60000) / 1000);
          return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
        };
        const lostTimer = getLostTimer();
        return (
          <div className="d-chat-main">
            {/* Chat header */}
            <div style={{ padding: "14px 24px", background: "var(--card)", borderBottom: active._lostItem ? "none" : "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <div style={{ position: "relative" }}>
                <Avatar user={other} size={38}/>
                {!active._lostItem && <div style={{ position: "absolute", bottom: 0, right: 0, width: 12, height: 12, borderRadius: "50%", background: "var(--success)", border: "2px solid var(--card)" }}/>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>{other.name}</div>
                <div style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>{active.tripFrom} → {active.tripTo}</div>
              </div>
            </div>
            {active._lostItem && (
              <div style={{ padding: "12px 24px", background: "linear-gradient(135deg, rgba(245,166,35,.12), rgba(245,166,35,.06))", borderBottom: "1px solid rgba(245,166,35,.2)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(245,166,35,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f5a623" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontFamily: "Syne", fontWeight: 700, color: "#f5a623" }}>{isDriverView ? "Forgot an Item" : "Temporary chat"}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>{isDriverView ? "Your passenger may have left something in your car" : "This chat closes automatically"}</div>
                </div>
                {lostTimer && <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 14, color: "#f5a623" }}>{lostTimer}</div>}
              </div>
            )}

            {/* Messages */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
              {messages.length === 0 && (
                <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, marginTop: 50 }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>👋</div>
                  <div style={{ fontFamily: "Syne", fontWeight: 600 }}>Say hi!</div>
                </div>
              )}
              {messages.map(m => {
                const mine = m.senderId === currentUser.id;
                return (
                  <div key={m.id} className="abubble" style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 6 }}>
                    {!mine && <Avatar user={other} size={24}/>}
                    <div style={{
                      maxWidth: "60%", padding: "10px 14px", fontSize: 14, lineHeight: 1.45,
                      borderRadius: mine ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                      background: mine ? "linear-gradient(135deg, #2b5be0, #5a80f0)" : "var(--card)",
                      color: mine ? "#fff" : "var(--ink)",
                      border: mine ? "none" : "1.5px solid var(--border)",
                      boxShadow: mine ? "0 4px 14px rgba(43,91,224,.3)" : "var(--shadow)",
                      wordBreak: "break-word", overflowWrap: "anywhere",
                    }}>
                      {m.text}
                      <div style={{ fontSize: 10, color: mine ? "rgba(255,255,255,.4)" : "var(--muted)", marginTop: 4, textAlign: mine ? "right" : "left" }}>
                        {m.ts?.toDate ? m.ts.toDate().toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" }) : "…"}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef}/>
            </div>

            {/* Input */}
            <div style={{ padding: "14px 24px", background: "var(--card)", borderTop: "1px solid var(--border)", display: "flex", gap: 10, flexShrink: 0 }}>
              <input value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMsg()} placeholder="Type a message…" style={{ flex: 1, padding: "12px 16px", borderRadius: 14, border: "1.5px solid var(--border)", background: "var(--paper)", fontSize: 14, color: "var(--ink)", outline: "none" }}/>
              <button onClick={sendMsg} style={{ width: 46, height: 46, borderRadius: 13, background: "var(--accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 12px rgba(43,91,224,.3)", border: "none", cursor: "pointer" }}>
                <span style={{ width: 18, height: 18 }}>{IC.send}</span>
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DesktopChat;
